import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Table,
  Input,
  Button,
  Space,
  Tag,
  Row,
  Col,
  Typography,
  Modal,
  Card,
  message,
  Tooltip,
} from "antd";
import type { InputRef } from "antd";
import dayjs from "dayjs";
import {
  SearchOutlined,
  CloudUploadOutlined,
  FileTextOutlined,
  CarOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  CalendarOutlined,
  LinkOutlined,
  InboxOutlined,
  DownloadOutlined,
  EyeOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  RotateLeftOutlined,
  RotateRightOutlined,
  CloseOutlined,
  ExpandOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import debounce from "lodash.debounce";
import { WORKFLOW_STATS, DELETE_WORKFLOW_TASK, FETCH_SIGNED_DOCUMENTS } from "../../services/apiEndPoints";
import { IWorkflowUpload, IWorkflowStats } from "../types/workflow.types";
import { useDateRange } from "../layout/AppLayout";
import axios from "../../services/axiosInstance";

const { Title, Text } = Typography;

const DOC_TYPE_LABELS: Record<string, { label: string; color: string }> = {
  final_weightment: { label: "Final Weightment", color: "blue" },
  challan_slip: { label: "Challan Slip", color: "green" },
  truck_sheet: { label: "Truck Sheet", color: "orange" },
  form_515: { label: "Form 515", color: "purple" },
  driver_arrival: { label: "Driver Arrival", color: "cyan" },
};

const STAT_CARDS = [
  {
    key: "totalUploads",
    label: "Total Uploads",
    icon: <CloudUploadOutlined />,
    color: "#1677ff",
    bg: "linear-gradient(135deg, #eff6ff, #dbeafe)",
    border: "#93c5fd",
  },
  {
    key: "todayUploads",
    label: "Today's Uploads",
    icon: <CalendarOutlined />,
    color: "#10b981",
    bg: "linear-gradient(135deg, #f0fdf4, #dcfce7)",
    border: "#6ee7b7",
  },
  {
    key: "uniqueTrucks",
    label: "Unique Trucks",
    icon: <CarOutlined />,
    color: "#8b5cf6",
    bg: "linear-gradient(135deg, #f5f3ff, #ede9fe)",
    border: "#c4b5fd",
  },
];

const WorkflowStatsComponent = () => {
  const { dateRange } = useDateRange();
  const [globalSearch, setGlobalSearch] = useState("");
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [data, setData] = useState<IWorkflowUpload[]>([]);
  const [stats, setStats] = useState<{ totalUploads: number; todayUploads: number; uniqueTrucks: number }>({
    totalUploads: 0,
    todayUploads: 0,
    uniqueTrucks: 0,
  });
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState("");
  const [lightboxLoading, setLightboxLoading] = useState(false);
  const [lightboxRecord, setLightboxRecord] = useState<IWorkflowUpload | null>(null);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const searchInput = useRef<InputRef>(null);

  const isPdf = (url: string) => url?.toLowerCase().includes(".pdf");

  const openLightbox = useCallback(async (record: IWorkflowUpload) => {
    setLightboxRecord(record);
    setLightboxOpen(true);
    setLightboxLoading(true);
    setZoom(1);
    setRotation(0);
    try {
      const res = await axios.get(
        `${FETCH_SIGNED_DOCUMENTS}?s3Url=${encodeURIComponent(record.s3Url)}`
      );
      const signedUrl = res?.data?.results || res?.data?.data || record.s3Url;
      setLightboxUrl(signedUrl);
    } catch {
      setLightboxUrl(record.s3Url);
    } finally {
      setLightboxLoading(false);
    }
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
    setLightboxUrl("");
    setLightboxRecord(null);
    setZoom(1);
    setRotation(0);
  }, []);

  const handleDownload = useCallback(async () => {
    if (!lightboxUrl) return;
    try {
      const res = await fetch(lightboxUrl);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      const ext = isPdf(lightboxUrl) ? "pdf" : "jpg";
      const vehicle = lightboxRecord?.vehicleNumber || "file";
      const docType = lightboxRecord?.docType || "document";
      a.download = `${vehicle}-${docType}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(lightboxUrl, "_blank");
    }
  }, [lightboxUrl, lightboxRecord]);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const fromDate = dateRange[0].format("YYYY-MM-DD");
      const toDate = dateRange[1].format("YYYY-MM-DD");
      const res = await axios.get(`${WORKFLOW_STATS}?fromDate=${fromDate}&toDate=${toDate}`);
      const d: IWorkflowStats = res?.data?.data;
      if (d) {
        setData(d.uploads || []);
        setStats({
          totalUploads: d.totalUploads,
          todayUploads: d.todayUploads,
          uniqueTrucks: new Set((d.uploads || []).map((u) => u.vehicleNumber)).size,
        });
      }
    } catch {
      message.error("Failed to fetch workflow stats");
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleDelete = useCallback(
    async (workflowTaskId: string) => {
      try {
        await axios.delete(DELETE_WORKFLOW_TASK(workflowTaskId));
        message.success("Workflow task deleted successfully");
        await fetchStats();
      } catch (error: any) {
        const msg = error?.response?.data?.error;
        message.error(msg || "Failed to delete workflow task");
      }
    },
    [fetchStats]
  );

  const handleSearch = useMemo(
    () =>
      debounce((selectedKeys: string[], confirm: () => void, dataIndex: string) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
      }, 300),
    []
  );

  const getColumnSearchProps = useCallback(
    (dataIndex: string) => ({
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters = () => {} }: any) => (
        <div style={{ padding: 8, width: 240 }}>
          <Input
            ref={searchInput}
            placeholder={`Search ${dataIndex}`}
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
            style={{ marginBottom: 8 }}
          />
          <Space>
            <Button type="primary" size="small" icon={<SearchOutlined />} onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}>
              Search
            </Button>
            <Button
              size="small"
              onClick={() => {
                clearFilters();
                setSearchText("");
              }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: any) => <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />,
      onFilter: (value: any, record: IWorkflowUpload) =>
        record[dataIndex as keyof IWorkflowUpload]?.toString().toLowerCase().includes(String(value).toLowerCase()),
      render: (text: any) =>
        searchedColumn === dataIndex ? (
          <Highlighter highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }} searchWords={[searchText]} autoEscape textToHighlight={text?.toString() || ""} />
        ) : (
          <span style={{ whiteSpace: "nowrap" }}>{text}</span>
        ),
    }),
    [searchText, searchedColumn, handleSearch]
  );

  const filteredData = useMemo(() => {
    if (!globalSearch) return data;
    return data.filter((item) => Object.values(item).join(" ").toLowerCase().includes(globalSearch.toLowerCase()));
  }, [data, globalSearch]);

  const columns = useMemo(
    () => [
      {
        title: <><CarOutlined style={{ fontSize: 12 }} /> Vehicle</>,
        dataIndex: "vehicleNumber",
        key: "vehicleNumber",
        ...getColumnSearchProps("vehicleNumber"),
        render: (v: string) => <Text strong style={{ fontSize: 13, color: "#1677ff" }}>{v}</Text>,
      },
      {
        title: <><FileTextOutlined style={{ fontSize: 12 }} /> Doc Type</>,
        dataIndex: "docType",
        key: "docType",
        width: 160,
        filters: Object.entries(DOC_TYPE_LABELS).map(([key, val]) => ({ text: val.label, value: key })),
        onFilter: (value: any, record: IWorkflowUpload) => record.docType === value,
        render: (docType: string) => {
          const config = DOC_TYPE_LABELS[docType] || { label: docType, color: "default" };
          return <Tag color={config.color}>{config.label}</Tag>;
        },
      },
      {
        title: <><CheckCircleOutlined style={{ fontSize: 12 }} /> Status</>,
        dataIndex: "status",
        key: "status",
        width: 100,
        align: "center" as const,
        filters: [
          { text: "Success", value: "success" },
          { text: "Failed", value: "failed" },
          { text: "Pending", value: "pending" },
        ],
        onFilter: (value: any, record: IWorkflowUpload) => record.status === value,
        render: (status: string) => (
          <Tag color={status === "success" ? "success" : status === "failed" ? "error" : "warning"}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </Tag>
        ),
      },
      {
        title: <><EyeOutlined style={{ fontSize: 12 }} /> Preview</>,
        dataIndex: "s3Url",
        key: "s3Url",
        width: 90,
        align: "center" as const,
        render: (url: string, record: IWorkflowUpload) =>
          url ? (
            <Tooltip title="Preview file">
              <div
                onClick={() => openLightbox(record)}
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 8,
                  overflow: "hidden",
                  cursor: "pointer",
                  border: "1px solid #e2e8f0",
                  background: "#f8fafc",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.2s ease",
                  margin: "0 auto",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "#1677ff";
                  e.currentTarget.style.boxShadow = "0 2px 8px rgba(22,119,255,0.15)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#e2e8f0";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                {isPdf(url) ? (
                  <FileTextOutlined style={{ fontSize: 20, color: "#ef4444" }} />
                ) : (
                  <img
                    src={url}
                    alt="preview"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                      (e.target as HTMLImageElement).parentElement!.innerHTML =
                        '<span style="font-size:20px;color:#94a3b8;">&#128444;</span>';
                    }}
                  />
                )}
              </div>
            </Tooltip>
          ) : (
            <Text type="secondary">--</Text>
          ),
      },
      {
        title: <><CalendarOutlined style={{ fontSize: 12 }} /> Uploaded</>,
        dataIndex: "createdAt",
        key: "createdAt",
        width: 150,
        sorter: (a: IWorkflowUpload, b: IWorkflowUpload) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
        render: (createdAt: string) => (
          <div>
            <Text style={{ whiteSpace: "nowrap", fontSize: 13 }}>{dayjs(createdAt).format("MMM D, YYYY")}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 11 }}>{dayjs(createdAt).format("h:mm A")}</Text>
          </div>
        ),
      },
      {
        title: "Actions",
        key: "actions",
        width: 80,
        align: "center" as const,
        render: (_: any, record: IWorkflowUpload) => (
          <Tooltip title="Delete">
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() =>
                Modal.confirm({
                  title: "Delete Workflow Task",
                  content: `Delete upload for ${record.vehicleNumber} (${DOC_TYPE_LABELS[record.docType]?.label || record.docType})?`,
                  okText: "Delete",
                  okType: "danger",
                  onOk: () => handleDelete(record.workflowTaskId),
                })
              }
            />
          </Tooltip>
        ),
      },
    ],
    [getColumnSearchProps, handleDelete]
  );

  return (
    <div>
      <Row justify="space-between" align="middle" className="page-header-row">
        <Col>
          <Title level={4} className="page-title">
            <CloudUploadOutlined style={{ marginRight: 8, color: "#1677ff" }} />
            Workflow Uploads
          </Title>
          <Text className="page-subtitle">Track and manage document workflow uploads</Text>
        </Col>
        <Col>
          <Button
            icon={<DownloadOutlined />}
            onClick={() => {
              if (!filteredData.length) {
                message.info("No records to download");
                return;
              }
              const headers = ["Vehicle Number", "Doc Type", "Status", "S3 URL", "Uploaded"];
              const rows = filteredData.map((r) => [
                r.vehicleNumber,
                DOC_TYPE_LABELS[r.docType]?.label || r.docType,
                r.status,
                r.s3Url || "",
                dayjs(r.createdAt).format("YYYY-MM-DD HH:mm"),
              ]);
              const csv = [headers, ...rows].map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
              const blob = new Blob([csv], { type: "text/csv" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `workflow-uploads-${dateRange[0].format("YYYY-MM-DD")}-to-${dateRange[1].format("YYYY-MM-DD")}.csv`;
              a.click();
              URL.revokeObjectURL(url);
            }}
          >
            Download CSV
          </Button>
        </Col>
      </Row>

      <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
        {STAT_CARDS.map((card) => (
          <Col key={card.key} xs={24} sm={8}>
            <Card
              className="stat-card"
              style={{ background: card.bg, border: `1px solid ${card.border}` }}
              styles={{ body: { padding: "14px 16px" } }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    background: `${card.color}18`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 18,
                    color: card.color,
                    flexShrink: 0,
                  }}
                >
                  {card.icon}
                </div>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: card.color, lineHeight: 1.1 }}>
                    {stats[card.key as keyof typeof stats]}
                  </div>
                  <div style={{ fontSize: 11, color: "#64748b", fontWeight: 500, lineHeight: 1.3 }}>{card.label}</div>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <div>
        <div className="table-toolbar">
          <Space wrap size={8}>
            <Input
              allowClear
              prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
              placeholder="Search uploads..."
              style={{ width: 260 }}
              value={globalSearch}
              onChange={(e) => setGlobalSearch(e.target.value)}
            />
          </Space>
        </div>

        <Table
          rowKey="workflowTaskId"
          size="middle"
          columns={columns}
          dataSource={filteredData}
          loading={{ spinning: loading, tip: "Loading uploads..." }}
          locale={{
            emptyText: (
              <div style={{ padding: "40px 0" }}>
                <InboxOutlined style={{ fontSize: 42, color: "#cbd5e1", marginBottom: 12 }} />
                <div style={{ color: "#64748b", fontSize: 14, fontWeight: 500 }}>No uploads found</div>
                <div style={{ color: "#94a3b8", fontSize: 13, marginTop: 4 }}>
                  {globalSearch ? "Try adjusting your search" : "No workflow uploads in this date range"}
                </div>
              </div>
            ),
          }}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
            showTotal: (total, range) => (
              <Text style={{ color: "#64748b", fontSize: 13 }}>
                Showing <Text strong>{range[0]}-{range[1]}</Text> of <Text strong>{total}</Text> upload{total !== 1 ? "s" : ""}
              </Text>
            ),
          }}
          scroll={{ x: "max-content" }}
        />
      </div>

      {/* ─── Lightbox Modal ─────────────────────────────── */}
      <Modal
        open={lightboxOpen}
        onCancel={closeLightbox}
        footer={null}
        width="90vw"
        centered
        destroyOnClose
        closeIcon={null}
        styles={{
          wrapper: {},
          mask: { background: "rgba(0,0,0,0.85)" },
          body: { padding: 0 },
          root: {},
          container: {
            background: "rgba(0,0,0,0.92)",
            borderRadius: 16,
            overflow: "hidden",
          },
        }}
        style={{ top: 20 }}
      >
        {/* Toolbar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 20px",
            borderBottom: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {lightboxRecord && (
              <>
                <Text style={{ color: "#fff", fontSize: 14, fontWeight: 600 }}>
                  {lightboxRecord.vehicleNumber}
                </Text>
                <Tag
                  color={DOC_TYPE_LABELS[lightboxRecord.docType]?.color || "default"}
                  style={{ margin: 0, borderRadius: 6 }}
                >
                  {DOC_TYPE_LABELS[lightboxRecord.docType]?.label || lightboxRecord.docType}
                </Tag>
              </>
            )}
          </div>

          <Space size={4}>
            {!isPdf(lightboxUrl) && (
              <>
                <Tooltip title="Zoom in">
                  <Button
                    type="text"
                    icon={<ZoomInOutlined />}
                    onClick={() => setZoom((z) => Math.min(z + 0.25, 5))}
                    style={{ color: "#fff" }}
                  />
                </Tooltip>
                <Tooltip title="Zoom out">
                  <Button
                    type="text"
                    icon={<ZoomOutOutlined />}
                    onClick={() => setZoom((z) => Math.max(z - 0.25, 0.25))}
                    style={{ color: "#fff" }}
                  />
                </Tooltip>
                <Tooltip title="Rotate left">
                  <Button
                    type="text"
                    icon={<RotateLeftOutlined />}
                    onClick={() => setRotation((r) => r - 90)}
                    style={{ color: "#fff" }}
                  />
                </Tooltip>
                <Tooltip title="Rotate right">
                  <Button
                    type="text"
                    icon={<RotateRightOutlined />}
                    onClick={() => setRotation((r) => r + 90)}
                    style={{ color: "#fff" }}
                  />
                </Tooltip>
                <Tooltip title="Reset">
                  <Button
                    type="text"
                    icon={<ExpandOutlined />}
                    onClick={() => { setZoom(1); setRotation(0); }}
                    style={{ color: "#fff" }}
                  />
                </Tooltip>
                <div
                  style={{
                    width: 1,
                    height: 20,
                    background: "rgba(255,255,255,0.15)",
                    margin: "0 4px",
                  }}
                />
              </>
            )}
            <Tooltip title="Download">
              <Button
                type="text"
                icon={<DownloadOutlined />}
                onClick={handleDownload}
                disabled={lightboxLoading}
                style={{ color: "#fff" }}
              />
            </Tooltip>
            <Tooltip title="Open in new tab">
              <Button
                type="text"
                icon={<LinkOutlined />}
                onClick={() => window.open(lightboxUrl, "_blank")}
                disabled={lightboxLoading}
                style={{ color: "#fff" }}
              />
            </Tooltip>
            <div
              style={{
                width: 1,
                height: 20,
                background: "rgba(255,255,255,0.15)",
                margin: "0 4px",
              }}
            />
            <Tooltip title="Close">
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={closeLightbox}
                style={{ color: "#fff" }}
              />
            </Tooltip>
          </Space>
        </div>

        {/* Content */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "75vh",
            maxHeight: "80vh",
            overflow: "auto",
            padding: 24,
          }}
        >
          {lightboxLoading ? (
            <div style={{ textAlign: "center" }}>
              <LoadingOutlined style={{ fontSize: 36, color: "#fff" }} />
              <div style={{ color: "#94a3b8", marginTop: 12, fontSize: 13 }}>
                Loading preview...
              </div>
            </div>
          ) : isPdf(lightboxUrl) ? (
            <iframe
              src={lightboxUrl}
              title="PDF Preview"
              style={{
                width: "100%",
                height: "75vh",
                border: "none",
                borderRadius: 8,
                background: "#fff",
              }}
            />
          ) : (
            <img
              src={lightboxUrl}
              alt="Preview"
              style={{
                maxWidth: "100%",
                maxHeight: "75vh",
                objectFit: "contain",
                borderRadius: 8,
                transform: `scale(${zoom}) rotate(${rotation}deg)`,
                transition: "transform 0.2s ease",
              }}
              onError={(e) => {
                (e.target as HTMLImageElement).src = "";
                (e.target as HTMLImageElement).alt = "Failed to load image";
              }}
            />
          )}
        </div>
      </Modal>
    </div>
  );
};

export default WorkflowStatsComponent;
